const apiBase = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '')
export const apiUrl = (path: string) => `${apiBase}${path}`
export const authTokenStorageKey = 'ych.rebuild.authToken'
export const accountUsernameStorageKey = 'ych.rebuild.loginUsername'
export const rememberLoginStorageKey = 'ych.rebuild.rememberLogin'

export function storeAuthToken(token: string, rememberLogin: boolean): void {
  const local = globalThis.localStorage
  const session = globalThis.sessionStorage
  local?.removeItem(authTokenStorageKey)
  session?.removeItem(authTokenStorageKey)
  const storage = rememberLogin ? local : session
  if (!storage) throw new Error('当前环境不支持浏览器会话存储')
  storage.setItem(authTokenStorageKey, token)
}

export function clearAuthToken(): void {
  globalThis.localStorage?.removeItem(authTokenStorageKey)
  globalThis.sessionStorage?.removeItem(authTokenStorageKey)
}

export function createIdempotencyKey(): string {
  const browserCrypto = globalThis.crypto
  if (typeof browserCrypto?.randomUUID === 'function') return browserCrypto.randomUUID()

  const bytes = new Uint8Array(16)
  if (typeof browserCrypto?.getRandomValues === 'function') {
    browserCrypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

export function normalizeQueryWhitespace(path: string): string {
  const hashIndex = path.indexOf('#')
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const queryIndex = withoutHash.indexOf('?')
  if (queryIndex < 0) return path
  const pathname = withoutHash.slice(0, queryIndex)
  const search = new URLSearchParams(withoutHash.slice(queryIndex + 1))
  const normalized = new URLSearchParams()
  search.forEach((value, key) => normalized.append(key, value.trim()))
  const query = normalized.toString()
  return `${pathname}${query ? `?${query}` : ''}${hash}`
}

export interface ApiValidationIssue {
  loc?: Array<string | number>
  msg?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
    readonly requestId?: string | null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let unauthorizedHandler: (() => void | Promise<void>) | null = null

export function setUnauthorizedHandler(handler: () => void | Promise<void>) {
  unauthorizedHandler = handler
}

export async function handleUnauthorized(): Promise<void> {
  if (unauthorizedHandler) await unauthorizedHandler()
}

export function getAuthToken(): string {
  return globalThis.sessionStorage?.getItem(authTokenStorageKey) || globalThis.localStorage?.getItem(authTokenStorageKey) || ''
}

export function readableDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && typeof item === 'object' ? (item as ApiValidationIssue).msg : ''))
      .filter(Boolean)
    if (messages.length) return messages.join('；')
  }
  return fallback
}

async function parseError(response: Response): Promise<ApiError> {
  const fallback = response.status === 403 ? '当前账号无权执行此操作' : `请求失败（HTTP ${response.status}）`
  let detail: unknown
  try {
    const payload = (await response.json()) as { detail?: unknown }
    detail = payload.detail
  } catch {
    detail = undefined
  }
  return new ApiError(
    readableDetail(detail, fallback),
    response.status,
    detail,
    response.headers.get('x-request-id'),
  )
}

export interface RequestOptions extends RequestInit {
  auth?: boolean
  idempotencyKey?: string
}

export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers)
  const method = (options.method || 'GET').toUpperCase()
  if (options.auth !== false && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', options.idempotencyKey || createIdempotencyKey())
  }
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.auth !== false) {
    const token = getAuthToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }
  let response: Response
  try {
    response = await fetch(apiUrl(normalizeQueryWhitespace(path)), { ...options, headers })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError('无法连接服务，请检查网络后重试', 0, error)
  }
  if (!response.ok) {
    const error = await parseError(response)
    if (response.status === 401 && options.auth !== false && unauthorizedHandler) {
      await handleUnauthorized()
    }
    throw error
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export async function requestBlob(path: string, options: RequestOptions = {}): Promise<{ blob: Blob; filename: string }> {
  const headers = new Headers(options.headers)
  const token = getAuthToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  let response: Response
  try {
    response = await fetch(apiUrl(normalizeQueryWhitespace(path)), { ...options, headers })
  } catch (error) {
    throw new ApiError('下载失败，请检查网络后重试', 0, error)
  }
  if (!response.ok) {
    const error = await parseError(response)
    if (response.status === 401) await handleUnauthorized()
    throw error
  }
  const disposition = response.headers.get('content-disposition') || ''
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]
  return {
    blob: await response.blob(),
    filename: encoded ? decodeURIComponent(encoded) : plain || '下载文件',
  }
}

export interface HealthResponse { status: string; system_name: string; system_subtitle: string }
export const getHealth = (signal?: AbortSignal) => requestJson<HealthResponse>('/api/health', { signal, auth: false })
